/**
 * Vashion — signature motion layer
 * Lightweight, dependency-free scroll-reveal + cursor glow.
 * Pairs with assets/vashion-fx.css. Fully disabled for
 * prefers-reduced-motion and on touch/coarse-pointer devices
 * (the cursor glow only — reveal-on-scroll still runs, gently).
 */
(function () {
  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function markSectionsForReveal() {
    var candidates = document.querySelectorAll(
      '.shopify-section:not(.vashion-fx-scanned)'
    );
    candidates.forEach(function (section, index) {
      section.classList.add('vashion-fx-scanned');
      // Skip header/footer/announcement chrome — only reveal page content.
      if (
        section.closest('.shopify-section-group-header-group') ||
        section.closest('.shopify-section-group-footer-group')
      ) {
        return;
      }
      section.classList.add('vashion-fx-reveal');
      section.style.setProperty('--vashion-fx-delay', String(Math.min(index, 4) * 70));
    });
  }

  function initReveal() {
    markSectionsForReveal();

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      document.querySelectorAll('.vashion-fx-reveal').forEach(function (el) {
        el.classList.add('vashion-fx-in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('vashion-fx-in');
            obs.unobserve(entry.target);
          }
        });
      },
      // threshold was 0.08 (8% of the section's own area must be visible to
      // trigger the fade-in). That works for normal-sized sections, but a
      // very tall one (e.g. a 50-product grid stretching ~9000px on mobile)
      // can never show 8% of itself at once inside an ~800px viewport — the
      // max achievable ratio is roughly viewport-height ÷ section-height,
      // which dips below 0.08 for a section that tall, so the observer
      // never fires and the whole section stays stuck at opacity:0 forever
      // (this is exactly what happened to the New Arrivals product grid:
      // real products in the DOM, just permanently invisible). threshold:0
      // fires as soon as any part of a section enters view, independent of
      // how tall that section is, while still fading in normal sections the
      // same way as before.
      { rootMargin: '0px 0px -8% 0px', threshold: 0 }
    );

    document.querySelectorAll('.vashion-fx-reveal:not(.vashion-fx-in)').forEach(function (el) {
      observer.observe(el);
    });
  }

  function initCursorGlow() {
    if (prefersReducedMotion) return;
    if (!window.matchMedia || !window.matchMedia('(pointer: fine)').matches) return;

    var glow = document.createElement('div');
    glow.className = 'vashion-fx-glow';
    document.body.appendChild(glow);

    var targetX = 0;
    var targetY = 0;
    var currentX = 0;
    var currentY = 0;
    var active = false;
    var rafId = null;

    function loop() {
      currentX += (targetX - currentX) * 0.12;
      currentY += (targetY - currentY) * 0.12;
      glow.style.transform = 'translate3d(' + currentX + 'px,' + currentY + 'px,0)';
      rafId = window.requestAnimationFrame(loop);
    }

    window.addEventListener(
      'pointermove',
      function (event) {
        targetX = event.clientX;
        targetY = event.clientY;
        if (!active) {
          active = true;
          glow.classList.add('vashion-fx-glow--active');
          if (!rafId) loop();
        }
      },
      { passive: true }
    );

    window.addEventListener('pointerleave', function () {
      active = false;
      glow.classList.remove('vashion-fx-glow--active');
    });
  }

  function initClickFeedback() {
    if (prefersReducedMotion) return;

    var selector =
      '.button, a.button, .vashion-bottom-nav__item, .card, .header__menu-item a, ' +
      '.multicolumn-card, .collection-list__item .card, .product-card';

    document.addEventListener(
      'pointerdown',
      function (event) {
        var target = event.target.closest(selector);
        if (!target) return;

        target.classList.add('vashion-fx-ripple-host');

        var rect = target.getBoundingClientRect();
        var size = Math.max(rect.width, rect.height);
        var ripple = document.createElement('span');
        ripple.className = 'vashion-fx-ripple';
        ripple.style.width = size + 'px';
        ripple.style.height = size + 'px';
        ripple.style.left = event.clientX - rect.left + 'px';
        ripple.style.top = event.clientY - rect.top + 'px';

        target.appendChild(ripple);

        window.setTimeout(function () {
          ripple.remove();
        }, 650);
      },
      { passive: true }
    );
  }

  /**
   * Announcement bar → continuous marquee.
   * Reads the text out of the announcement bar's own blocks (single-block
   * div or multi-block slideshow-component — both render one
   * `.announcement-bar__message` per announcement) and builds a duplicated,
   * seamlessly-looping track from it. The original stays in the DOM,
   * visually hidden via Dawn's own `.visually-hidden` utility, so screen
   * readers and the theme editor both keep working exactly as before.
   */
  function buildMarquee() {
    var announcementBar = document.querySelector('.utility-bar .announcement-bar');
    if (!announcementBar) return;

    var messages = announcementBar.querySelectorAll('.announcement-bar__message');
    if (!messages.length) return;

    var items = [];
    messages.forEach(function (message) {
      var span = message.querySelector('span');
      var text = (span || message).textContent.replace(/\s+/g, ' ').trim();
      if (text) items.push(text);
    });
    if (!items.length) return;

    var track = document.createElement('div');
    track.className = 'vashion-marquee__track';

    function appendSet() {
      items.forEach(function (text) {
        var item = document.createElement('span');
        item.className = 'vashion-marquee__item';
        item.textContent = text;
        track.appendChild(item);
      });
    }

    // Duplicate the set once so a -50% translate loops with no visible seam.
    appendSet();
    appendSet();

    var marquee = document.createElement('div');
    marquee.className = 'vashion-marquee';
    marquee.setAttribute('aria-hidden', 'true');
    marquee.appendChild(track);

    announcementBar.classList.add('visually-hidden');
    announcementBar.insertAdjacentElement('afterend', marquee);
    announcementBar.dataset.vashionMarqueeReady = 'true';
  }

  function teardownMarquee() {
    document.querySelectorAll('.vashion-marquee').forEach(function (el) {
      el.remove();
    });
    document.querySelectorAll('.utility-bar .announcement-bar').forEach(function (bar) {
      bar.classList.remove('visually-hidden');
      delete bar.dataset.vashionMarqueeReady;
    });
  }

  function initMarquee() {
    if (prefersReducedMotion) return;
    buildMarquee();
  }

  /**
   * Thin scroll-progress hairline, fixed to the very top of the viewport,
   * above the sticky header. No libraries — a single rAF-throttled scroll
   * listener updates the fill width as a percentage of page height.
   */
  function initScrollProgress() {
    if (document.querySelector('.vashion-scroll-progress')) return;

    var track = document.createElement('div');
    track.className = 'vashion-scroll-progress';
    track.setAttribute('aria-hidden', 'true');

    var fill = document.createElement('div');
    fill.className = 'vashion-scroll-progress__bar';
    track.appendChild(fill);
    document.body.appendChild(track);

    var ticking = false;

    function update() {
      var doc = document.documentElement;
      var scrollTop = window.pageYOffset || doc.scrollTop || 0;
      var maxScroll = (doc.scrollHeight - doc.clientHeight) || 1;
      var progress = Math.min(1, Math.max(0, scrollTop / maxScroll));
      fill.style.width = (progress * 100) + '%';
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  function init() {
    initReveal();
    initCursorGlow();
    initClickFeedback();
    initMarquee();
    initScrollProgress();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan when sections are added/reordered in the theme editor.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      markSectionsForReveal();
      document.querySelectorAll('.vashion-fx-reveal:not(.vashion-fx-in)').forEach(function (el) {
        el.classList.add('vashion-fx-in');
      });

      // The announcement bar was re-rendered by the editor (text/blocks
      // changed, or the section itself reloaded) — rebuild the marquee from
      // the fresh markup rather than leaving a stale/duplicated one.
      if (event.target && event.target.querySelector && event.target.querySelector('.announcement-bar')) {
        teardownMarquee();
        initMarquee();
      }
    });
  }
})();
